import React from "react";
import dayjs from "dayjs";
import { isMobileViewport } from "../../utils/responsive";
import EmptyState from "../common/empty_state";

const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "";
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const getTotalBalance = (record) => {
    const total = Number(record?.total_balance);
    if (Number.isFinite(total)) return total;
    return Number(record?.risky_balance_krw || 0) + Number(record?.fiat_balance || 0);
};

const LISTING_SNIPE = "ListingSnipe";

const buildTotalSeries = (balanceHistory) => {
    const totalBalanceMap = new Map();
    (balanceHistory || []).forEach((exchangeArr) => {
        (exchangeArr || []).forEach((item) => {
            if (!item?.created) return;
            if (item.strategy_name === LISTING_SNIPE) return;
            const hourKey = dayjs(item.created).startOf("hour").valueOf();
            const current = totalBalanceMap.get(hourKey) || 0;
            totalBalanceMap.set(hourKey, current + Number(item.total_balance || 0));
        });
    });
    return Array.from(totalBalanceMap.entries()).map(([time, totalBalance]) => ({ created: new Date(time), total_balance: totalBalance }));
};

const buildMonthlySummary = (balanceHistory) => {
    const sortedHistory = (balanceHistory || []).filter((item) => item && item.created).slice().sort((a, b) => new Date(a.created) - new Date(b.created));
    const monthlyMap = new Map();
    sortedHistory.forEach((item) => {
        const monthKey = dayjs(item.created).format("YYYY-MM");
        if (!monthlyMap.has(monthKey)) monthlyMap.set(monthKey, { first: item, end: item });
        else { const current = monthlyMap.get(monthKey); monthlyMap.set(monthKey, { ...current, end: item }); }
    });
    const sortedMonths = Array.from(monthlyMap.keys()).sort();
    let prevEndTotal = null;
    return sortedMonths.map((month) => {
        const range = monthlyMap.get(month);
        const endTotal = getTotalBalance(range.end);
        const startTotal = prevEndTotal !== null ? prevEndTotal : getTotalBalance(range.first);
        const profitLoss = endTotal - startTotal;
        const profitLossRate = startTotal !== 0 ? (profitLoss / startTotal) * 100 : 0;
        prevEndTotal = endTotal;
        return { month, startTotal, endTotal, profitLoss, profitLossRate };
    });
};

const buildMonthlySummaryFromApi = (summary) =>
    (summary || []).map((row) => ({
        month: row?.month,
        startTotal: Number(row?.start_total_balance || 0),
        endTotal: Number(row?.end_total_balance || 0),
        profitLoss: Number(row?.profit_loss_amount || 0),
        profitLossRate: Number(row?.profit_loss_rate || 0),
    }));

const buildTotalSummaryFromApi = (summary) => {
    const allMonths = new Set();
    const pairMonthlies = [];
    (summary || []).forEach((pair) => {
        const monthMap = {};
        (pair?.summary || []).forEach((row) => {
            if (!row?.month) return;
            allMonths.add(row.month);
            monthMap[row.month] = { start: Number(row?.start_total_balance || 0), end: Number(row?.end_total_balance || 0) };
        });
        pairMonthlies.push(monthMap);
    });
    const sortedMonths = Array.from(allMonths).sort();
    return sortedMonths.map((month) => {
        let totalStart = 0;
        let totalEnd = 0;
        pairMonthlies.forEach((pairData) => {
            if (pairData[month]) { totalStart += pairData[month].start; totalEnd += pairData[month].end; }
            else {
                const prevMonths = sortedMonths.filter((m) => m < month);
                for (let i = prevMonths.length - 1; i >= 0; i--) {
                    if (pairData[prevMonths[i]]) { const carried = pairData[prevMonths[i]].end; totalStart += carried; totalEnd += carried; break; }
                }
            }
        });
        const profitLoss = totalEnd - totalStart;
        const profitLossRate = totalStart !== 0 ? (profitLoss / totalStart) * 100 : 0;
        return { month, startTotal: totalStart, endTotal: totalEnd, profitLoss, profitLossRate };
    });
};

class MonthlySummaryTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = { page: 1 };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.balanceHistory !== this.props.balanceHistory ||
            prevProps.summary !== this.props.summary) {
            this.setState({ page: 1 });
        }
    }

    handlePageChange = (nextPage, totalPages) => {
        this.setState({ page: Math.min(Math.max(nextPage, 1), totalPages) });
    };

    render() {
        const isMobile = isMobileViewport();
        const summaryData = Array.isArray(this.props.summary) ? this.props.summary : [];
        let dataSource = [];
        if (this.props.mode === "total") {
            dataSource = summaryData.length > 0 ? buildTotalSummaryFromApi(summaryData) : buildMonthlySummary(buildTotalSeries(this.props.balanceHistory));
        } else if (summaryData.length > 0) {
            dataSource = buildMonthlySummaryFromApi(summaryData);
        } else {
            dataSource = buildMonthlySummary(this.props.balanceHistory);
        }
        const sortedData = dataSource.slice().sort((a, b) => String(b?.month || "").localeCompare(String(a?.month || "")));
        const pageSize = isMobile ? 6 : 10;
        const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
        const currentPage = Math.min(this.state.page, totalPages);
        const pageData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        return (
            <div className="w-full rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-content-secondary">월별 손익 요약</h3>
                    <div className="font-num text-xs text-content-muted">{sortedData.length}개월</div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-full table-fixed border-collapse text-xs sm:text-sm">
                        <thead className="bg-surface-elevated text-xs font-semibold uppercase tracking-wider text-content-secondary">
                            <tr>
                                <th className="w-20 px-2 py-2 text-left sm:px-3">월</th>
                                <th className="hidden sm:table-cell w-32 px-2 py-2 text-center sm:px-3">시작 잔고</th>
                                <th className="w-32 px-2 py-2 text-center sm:px-3">마감 잔고</th>
                                <th className="w-28 px-2 py-2 text-center sm:px-3">손익금</th>
                                <th className="w-20 px-2 py-2 text-center sm:px-3">손익률</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageData.map((row) => {
                                const profitClass = row.profitLoss >= 0 ? "text-gain" : "text-loss";
                                return (
                                    <tr key={row.month} className="border-b border-surface-border transition-colors hover:bg-surface-elevated">
                                        <td className="whitespace-nowrap px-2 py-2 text-left text-content-secondary sm:px-3 sm:py-3">{row.month}</td>
                                        <td className="hidden sm:table-cell whitespace-nowrap px-2 py-2 text-center font-num text-content sm:px-3 sm:py-3">₩{formatNumber(row.startTotal, 0)}</td>
                                        <td className="whitespace-nowrap px-2 py-2 text-center font-num text-content sm:px-3 sm:py-3">₩{formatNumber(row.endTotal, 0)}</td>
                                        <td className={`whitespace-nowrap px-2 py-2 text-center font-num sm:px-3 sm:py-3 ${profitClass}`}>₩{formatNumber(row.profitLoss, 0)}</td>
                                        <td className={`whitespace-nowrap px-2 py-2 text-center font-num sm:px-3 sm:py-3 ${profitClass}`}>{Number(row.profitLossRate).toFixed(2)}%</td>
                                    </tr>
                                );
                            })}
                            {pageData.length === 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <EmptyState title="표시할 데이터가 없습니다" />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                    <button type="button" onClick={() => this.handlePageChange(currentPage - 1, totalPages)} disabled={currentPage <= 1} className="rounded-md border border-surface-border px-3 py-1 text-xs font-semibold text-content-secondary disabled:cursor-not-allowed disabled:text-content-muted transition-colors hover:bg-surface-hover">이전</button>
                    <div className="font-num text-xs text-content-muted">{currentPage} / {totalPages}</div>
                    <button type="button" onClick={() => this.handlePageChange(currentPage + 1, totalPages)} disabled={currentPage >= totalPages} className="rounded-md border border-surface-border px-3 py-1 text-xs font-semibold text-content-secondary disabled:cursor-not-allowed disabled:text-content-muted transition-colors hover:bg-surface-hover">다음</button>
                </div>
            </div>
        );
    }
}

export default MonthlySummaryTable;
